import Accordion from '@ui/Accordion';
import Checkbox from '@ui/Checkbox';
import { usesPathFilters } from '@util/pathFilters';
import { slugify } from '@util/urlHelpers';
import type { FacetCountValue } from 'models/product';
import { usePathname } from 'next/navigation';
import type { FilterItem } from '../Filters';
import { cn, isMobile } from '@util/index';

type Props = {
  filterOptions?: FilterItem[];
  onChange: (keys: string[]) => void;
  title: string;
  filterState: string[];
  pendingFilters?: string[];
  noFiltersMessage?: string;
  noFiltersClassName?: string;
  startOpen?: boolean;
  children?: React.ReactNode;
  facet_counts?: FacetCountValue[];
  hideBottomBorder?: boolean;
  showSelectedFirst?: boolean;
};

const CheckboxFilterSection = ({
  filterOptions,
  onChange,
  title,
  filterState,
  pendingFilters = [],
  noFiltersMessage,
  noFiltersClassName,
  startOpen,
  facet_counts,
  children,
  hideBottomBorder = false,
  showSelectedFirst = false,
}: Props) => {
  const pathname = usePathname();

  const handleCheckboxChange = (key: string) => {
    const currentFilters =
      isMobile() && pendingFilters.length > 0 ? pendingFilters : filterState;

    const filterExists = currentFilters.some(
      (filter) =>
        typeof filter === 'string' &&
        slugify(filter.toLowerCase()) === slugify(key.toLowerCase())
    );

    const newFilters = filterExists
      ? currentFilters.filter(
          (filter) =>
            typeof filter === 'string' &&
            slugify(filter.toLowerCase()) !== slugify(key.toLowerCase())
        )
      : [...currentFilters, key];

    onChange(newFilters);
  };

  const isSelected = (optKey: string) => {
    const normalizedKey = slugify(optKey.toLowerCase());
    return filterState?.some(
      (x) => typeof x === 'string' && slugify(x.toLowerCase()) === normalizedKey
    );
  };

  // only ever called for category pages, see the link prop below
  function getHref(opt: string) {
    const segments = pathname?.split('/');
    if (!segments) return '/';
    const path = segments.slice(0, segments.length - 1);
    const lastCat = segments[segments.length - 1];
    opt = opt.replace('%', '-percent').replace(' ', '-');
    opt = opt.replace('&', '');
    opt = opt.replace("'", '').replace(')', '');
    let optSlug = '';
    optSlug =
      title.includes('Size') && !title.includes('Engine')
        ? `size-${opt}`
        : slugify(opt);
    const final =
      (filterState.length > 0 ? '/filters' : '') +
      path?.join('/') +
      '/' +
      optSlug +
      `${lastCat ? `-${lastCat}` : ''}`;
    return final;
  }

  return (
    <div className={cn(!hideBottomBorder && 'border-b border-zinc-300')}>
      <Accordion
        title={title}
        titleClassName="!text-[1.8rem] !font-semibold"
        startOpen={startOpen}
        arrowClassName="w-16 sm:w-9"
      >
        {children}
        <div className="flex gap-3 overflow-x-auto overflow-y-hidden px-[1rem] pb-[2rem] sm:max-h-[45rem] sm:flex-col sm:gap-4 sm:overflow-y-auto sm:overflow-x-hidden">
          {filterOptions && filterOptions.length > 0 ? (
            filterOptions
              .sort((a, b) => {
                if (showSelectedFirst) {
                  const aSelected = isSelected(a.key);
                  const bSelected = isSelected(b.key);
                  if (aSelected && !bSelected) return -1;
                  if (!aSelected && bSelected) return 1;
                }
                return 0;
              })
              .map((opt) => {
                const optionSelected = isSelected(opt.key);
                const hasFacetCount = !!facet_counts?.find(
                  (c) => c.value === opt.key
                );

                return (
                  <div className="flex items-center gap-x-2" key={opt.key}>
                    <button
                      type="button"
                      className={cn(
                        'flex shrink-0 items-center justify-center rounded-xl px-4 py-2 text-[1.8rem] font-medium sm:hidden',
                        optionSelected || hasFacetCount
                          ? 'bg-black text-white'
                          : 'bg-[#F1F1F1] text-black'
                      )}
                      onClick={() => handleCheckboxChange(opt.key)}
                    >
                      {opt.label}
                      {hasFacetCount && (
                        <span className="ml-2 text-[1.5rem] text-brand-dark-gray">
                          (
                          {
                            facet_counts?.find((c) => c.value === opt.key)
                              ?.count
                          }
                          )
                        </span>
                      )}
                    </button>
                    <div className="hidden sm:flex sm:items-center sm:gap-x-2">
                      <Checkbox
                        className="rounded-md !outline-none !ring-0"
                        label={opt.label}
                        link={
                          usesPathFilters(pathname ?? '') &&
                          title !== 'Ship From' &&
                          title !== 'Ship To' &&
                          !!facet_counts?.find((c) => c.value === opt.key)
                            ?.count
                            ? getHref(opt.key)
                            : undefined
                        }
                        onChange={() => handleCheckboxChange(opt.key)}
                        selected={isSelected(opt.key)}
                        key={JSON.stringify(isSelected(opt.key))}
                      />
                      {hasFacetCount && (
                        <span className="text-[1.2rem] text-brand-dark-gray">
                          (
                          {
                            facet_counts?.find((c) => c.value === opt.key)
                              ?.count
                          }
                          )
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
          ) : (
            <div className={noFiltersClassName}>
              <p className="text-center">{noFiltersMessage}</p>
            </div>
          )}
        </div>
      </Accordion>
    </div>
  );
};

export default CheckboxFilterSection;
